import React from 'react';
import AddressAutocomplete from '../AddressAutocomplete';
import PayRateGate from '../shared/PayRateGate';
import { toast } from '../ui/sonner';
import { AlertCircle, Camera, CheckCircle, ClipboardList, DollarSign, Lightbulb, Pen, Pill, Plus, RefreshCw, Type, Upload, Wand2, X } from 'lucide-react';

// Extracted from EmployerDashboard.js — receives everything it needs via `ctx`.
export default function EmployerJobFormModal({ ctx }) {
  const { addJobTask, aiJobLoading, basicCanvasRef, basicSignatureMode, basicTypedSignature, clearBasicCanvas, drawBasic, generateJobDescription, handleCreateJob, handleJobMedicationToggle, jobForm, newJobTask, removeJobTask, setBasicSignatureMode, setBasicTypedSignature, setJobForm, setNewJobTask, setShowJobForm, showJobForm, startBasicDrawing, stopBasicDrawing, suggestJobTasks } = ctx;
  return (
    <>
    {showJobForm && (
      <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] flex flex-col shadow-2xl">
          <div className="flex items-center justify-between p-6 border-b border-slate-200 flex-shrink-0 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-t-2xl">
            <div>
              <h3 className="text-xl font-bold text-white">Post New Job</h3>
              <p className="text-sm text-blue-100 mt-1">Create a new job posting with AI assistance</p>
            </div>
            <button onClick={() => setShowJobForm(false)} className="p-2 hover:bg-white/20 rounded-lg transition">
              <X className="w-5 h-5 text-white" />
            </button>
          </div>
          
          <form onSubmit={handleCreateJob} className="p-6 space-y-5 overflow-y-auto flex-1">
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">Job Title *</label>
              <input
                type="text"
                value={jobForm.title}
                onChange={(e) => setJobForm({ ...jobForm, title: e.target.value })}
                className="w-full h-12 px-4 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 focus:outline-none focus:border-blue-500 focus:bg-white focus:ring-4 focus:ring-blue-500/10 transition-all"
                placeholder="e.g., Registered Nurse - Night Shift"
                required
                data-testid="job-title-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">Category</label>
                <select
                  value={jobForm.category}
                  onChange={(e) => setJobForm({ ...jobForm, category: e.target.value })}
                  className="w-full h-12 px-4 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 focus:outline-none focus:border-blue-500 focus:bg-white transition-all"
                  required
                >
                  <option value="remote">Remote</option>
                  <option value="onsite">Onsite</option>
                  <option value="hybrid">Hybrid</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">Pay Rate ($/hr)</label>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <PayRateGate>
                  <input
                    type="number"
                    value={jobForm.pay_rate}
                    onChange={(e) => setJobForm({ ...jobForm, pay_rate: e.target.value })}
                    className="w-full h-12 pl-10 pr-4 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 focus:outline-none focus:border-blue-500 focus:bg-white transition-all disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-400"
                    placeholder="25.00"
                    required
                    min="1"
                    step="0.01"
                    data-testid="job-pay-input"
                  />
                  </PayRateGate>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">Start Time *</label>
                <input
                  type="datetime-local"
                  value={jobForm.start_time}
                  onChange={(e) => setJobForm({ ...jobForm, start_time: e.target.value })}
                  className="w-full h-12 px-4 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 focus:outline-none focus:border-blue-500 focus:bg-white transition-all"
                  required
                  data-testid="job-start-time-input"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">End Time *</label>
                <input
                  type="datetime-local"
                  value={jobForm.end_time}
                  onChange={(e) => setJobForm({ ...jobForm, end_time: e.target.value })}
                  className="w-full h-12 px-4 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 focus:outline-none focus:border-blue-500 focus:bg-white transition-all"
                  required
                  data-testid="job-end-time-input"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">Location Address *</label>
              <AddressAutocomplete
                value={jobForm.location.address}
                onChange={(text) => setJobForm({ ...jobForm, location: { ...jobForm.location, address: text } })}
                onSelect={(s) => setJobForm({
                  ...jobForm,
                  location: {
                    ...jobForm.location,
                    address: s.label,
                    lat: s.lat,
                    lng: s.lng,
                    city: s.city,
                    state: s.state,
                    zip: s.zip,
                  },
                })}
                placeholder="Start typing a US address…"
                testId="job-location-autocomplete"
                required
              />
            </div>

            {/* Description with AI Generate */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-semibold text-slate-700">Description</label>
                <button
                  type="button"
                  onClick={generateJobDescription}
                  disabled={aiJobLoading}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg hover:shadow-md disabled:opacity-50 transition"
                  data-testid="job-ai-generate-btn"
                >
                  {aiJobLoading ? <RefreshCw className="w-3 h-3 animate-spin" /> : <Wand2 className="w-3 h-3" />}
                  AI Generate
                </button>
              </div>
              <textarea
                value={jobForm.description}
                onChange={(e) => setJobForm({ ...jobForm, description: e.target.value })}
                className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 focus:outline-none focus:border-blue-500 focus:bg-white transition-all resize-none"
                rows="3"
                placeholder="Describe the job responsibilities... or click AI Generate"
                required
                data-testid="job-description-input"
              />
            </div>

            {/* Medication Administration Required */}
            <div className={`p-4 rounded-xl border-2 transition ${
              jobForm.requires_medication 
                ? 'border-red-500 bg-red-50' 
                : 'border-slate-200 bg-slate-50'
            }`}>
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={jobForm.requires_medication}
                  onChange={(e) => handleJobMedicationToggle(e.target.checked)}
                  className="w-5 h-5 text-red-600 border-slate-300 rounded focus:ring-red-500"
                  data-testid="job-medication-checkbox"
                />
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <Pill className={`w-5 h-5 ${jobForm.requires_medication ? 'text-red-600' : 'text-slate-400'}`} />
                    <span className={`font-medium ${jobForm.requires_medication ? 'text-red-800' : 'text-slate-700'}`}>
                      Medication Administration Required
                    </span>
                    {jobForm.requires_medication && (
                      <span className="px-2 py-0.5 bg-red-600 text-white text-xs font-bold rounded-full">
                        HIGH PRIORITY
                      </span>
                    )}
                  </div>
                  <p className={`text-sm mt-1 ${jobForm.requires_medication ? 'text-red-700' : 'text-slate-500'}`}>
                    Check if staff is expected to administer medication during this job
                  </p>
                </div>
              </label>
            </div>

            {/* Expected Tasks/Activities Section */}
            <div className="border border-slate-200 rounded-xl overflow-hidden">
              <div className="px-4 py-3 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
                <div>
                  <h4 className="font-medium text-slate-800 flex items-center gap-2">
                    <ClipboardList className="w-5 h-5 text-amber-600" />
                    Expected Tasks/Activities
                  </h4>
                  <p className="text-sm text-slate-500 mt-1">Add tasks to complete during this job</p>
                </div>
                <button
                  type="button"
                  onClick={suggestJobTasks}
                  disabled={aiJobLoading}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-lg hover:shadow-md disabled:opacity-50 transition"
                  data-testid="job-ai-tasks-btn"
                >
                  {aiJobLoading ? <RefreshCw className="w-3 h-3 animate-spin" /> : <Lightbulb className="w-3 h-3" />}
                  AI Suggest Tasks
                </button>
              </div>
              
              <div className="p-4 space-y-3">
                {/* Task List */}
                {jobForm.expected_tasks.length > 0 && (
                  <div className="space-y-2 mb-4">
                    {jobForm.expected_tasks.map((task, idx) => (
                      <div 
                        key={task.id || task.title || idx} 
                        className={`flex items-center justify-between p-3 rounded-lg border ${
                          task.is_medication 
                            ? 'bg-red-50 border-red-300' 
                            : 'bg-white border-slate-200'
                        }`}
                        data-testid={`job-task-${idx}`}
                      >
                        <div className="flex items-center gap-3">
                          {task.is_medication ? (
                            <Pill className="w-5 h-5 text-red-600" />
                          ) : (
                            <CheckCircle className="w-5 h-5 text-emerald-600" />
                          )}
                          <span className={task.is_medication ? 'text-red-800 font-medium' : 'text-slate-700'}>
                            {task.name}
                          </span>
                          {task.is_medication && (
                            <span className="px-2 py-0.5 bg-red-600 text-white text-xs font-bold rounded-full">
                              MED
                            </span>
                          )}
                        </div>
                        <button
                          type="button"
                          onClick={() => removeJobTask(idx)}
                          className="p-1 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded transition"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {/* Add New Task */}
                <div className="flex gap-2">
                  <div className="flex-1">
                    <input
                      type="text"
                      value={newJobTask.name}
                      onChange={(e) => setNewJobTask({ ...newJobTask, name: e.target.value })}
                      className="w-full px-4 py-2.5 border border-slate-300 rounded-lg text-slate-800"
                      placeholder="Enter task name..."
                      onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addJobTask())}
                      data-testid="job-new-task-input"
                    />
                  </div>
                  <label className="flex items-center gap-2 px-3 py-2 bg-slate-100 rounded-lg cursor-pointer hover:bg-slate-200 transition">
                    <input
                      type="checkbox"
                      checked={newJobTask.is_medication}
                      onChange={(e) => setNewJobTask({ ...newJobTask, is_medication: e.target.checked })}
                      className="w-4 h-4 text-red-600 border-slate-300 rounded"
                      data-testid="job-task-med-checkbox"
                    />
                    <Pill className={`w-4 h-4 ${newJobTask.is_medication ? 'text-red-600' : 'text-slate-400'}`} />
                  </label>
                  <button
                    type="button"
                    onClick={addJobTask}
                    className="px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
                    data-testid="job-add-task-btn"
                  >
                    <Plus className="w-5 h-5" />
                  </button>
                </div>
                <p className="text-xs text-slate-500 mt-1">
                  Toggle the pill icon to mark a task as medication-related
                </p>
              </div>
            </div>

            {/* Allow Photo Uploads Option */}
            <div className="p-4 bg-indigo-50 rounded-xl border border-indigo-200">
              <div className="flex items-start gap-3">
                <input
                  type="checkbox"
                  id="basic_allow_photo_uploads"
                  checked={jobForm.allow_photo_uploads || false}
                  onChange={(e) => setJobForm({ ...jobForm, allow_photo_uploads: e.target.checked })}
                  className="w-5 h-5 mt-0.5 rounded border-gray-300 text-indigo-600"
                  data-testid="job-photo-upload-checkbox"
                />
                <div className="flex-1">
                  <label htmlFor="basic_allow_photo_uploads" className="text-sm font-medium text-indigo-900 cursor-pointer">
                    Allow Workers to Upload Photos/Images
                  </label>
                  <p className="text-sm text-indigo-700 mt-1">
                    When enabled, workers can attach photos as proof of task completion.
                  </p>
                  {jobForm.allow_photo_uploads && (
                    <div className="mt-2 p-2 bg-white rounded-lg border border-indigo-200">
                      <p className="text-xs text-indigo-600 flex items-center gap-2">
                        <Camera className="w-4 h-4" />
                        Workers can upload up to 5 images per task submission
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Task Verification Required Section */}
            <div className="p-5 bg-amber-50 rounded-xl border border-amber-200">
              <div className="flex items-start gap-3 mb-4">
                <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-amber-800">Task Verification Required</p>
                  <p className="text-sm text-amber-700 mt-1">
                    A staff member from the program or assigned work location must verify if all tasks are completed.
                    The verifying staff will provide their <strong>name</strong> and <strong>signature</strong> on the worker's portal.
                  </p>
                </div>
              </div>
              
              {/* Manager Acknowledgment Section */}
              <div className="mt-4 pt-4 border-t border-amber-200">
                <p className="text-sm font-semibold text-amber-900 mb-3">Manager Acknowledgment</p>
                
                {/* Status Badge */}
                {jobForm.task_verification?.requirement_accepted !== null && (
                  <div className={`mb-4 p-3 rounded-lg ${
                    jobForm.task_verification.requirement_accepted 
                      ? 'bg-green-100 border border-green-300' 
                      : 'bg-red-100 border border-red-300'
                  }`}>
                    <p className={`text-sm font-medium ${
                      jobForm.task_verification.requirement_accepted 
                        ? 'text-green-800' 
                        : 'text-red-800'
                    }`}>
                      {jobForm.task_verification.requirement_accepted 
                        ? '✓ Task verification requirement ACCEPTED' 
                        : '✗ Task verification requirement DENIED'}
                    </p>
                    <p className="text-xs text-gray-600 mt-1">
                      Signed by: {jobForm.task_verification.manager_name}
                    </p>
                  </div>
                )}
                
                {/* Name and Signature Fields */}
                <div className="space-y-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-amber-800 mb-1">Manager Name *</label>
                    <input
                      type="text"
                      value={jobForm.task_verification?.manager_name || ''}
                      onChange={(e) => setJobForm({
                        ...jobForm,
                        task_verification: {
                          ...jobForm.task_verification,
                          manager_name: e.target.value,
                          requirement_accepted: null
                        }
                      })}
                      disabled={jobForm.task_verification?.requirement_accepted !== null}
                      className={`w-full px-3 py-2 border border-amber-300 rounded-lg focus:ring-2 focus:ring-amber-500 bg-white text-gray-900 ${
                        jobForm.task_verification?.requirement_accepted !== null ? 'bg-gray-100 cursor-not-allowed' : ''
                      }`}
                      placeholder="Enter your full name"
                      data-testid="job-manager-name-input"
                    />
                  </div>
                  
                  {/* E-Signature Section */}
                  <div>
                    <label className="block text-sm font-medium text-amber-800 mb-2">E-Signature *</label>
                    
                    {/* Show locked signature if already accepted/denied */}
                    {jobForm.task_verification?.requirement_accepted !== null ? (
                      <div className={`p-4 rounded-lg border-2 ${
                        jobForm.task_verification.requirement_accepted 
                          ? 'bg-green-50 border-green-300' 
                          : 'bg-red-50 border-red-300'
                      }`}>
                        <div className="flex items-center gap-2 mb-2">
                          <CheckCircle className={`w-4 h-4 ${
                            jobForm.task_verification.requirement_accepted ? 'text-green-600' : 'text-red-600'
                          }`} />
                          <span className={`text-sm font-medium ${
                            jobForm.task_verification.requirement_accepted ? 'text-green-800' : 'text-red-800'
                          }`}>
                            Signature Saved & Locked
                          </span>
                        </div>
                        {jobForm.task_verification.manager_signature?.startsWith('data:') ? (
                          <img 
                            src={jobForm.task_verification.manager_signature} 
                            alt="Saved signature" 
                            className="max-h-16 border border-gray-200 rounded bg-white p-2"
                          />
                        ) : (
                          <p className="text-xl text-blue-800 bg-white p-3 rounded border border-gray-200" 
                             style={{ fontFamily: "'Brush Script MT', 'Segoe Script', cursive" }}>
                            {jobForm.task_verification.manager_signature}
                          </p>
                        )}
                        <p className="text-xs text-gray-500 mt-2">
                          Signed on: {new Date(jobForm.task_verification.verified_at).toLocaleString()}
                        </p>
                      </div>
                    ) : (
                      <>
                        {/* Type/Draw Toggle */}
                        <div className="flex mb-3 bg-amber-100 rounded-lg p-1">
                          <button
                            type="button"
                            onClick={() => setBasicSignatureMode('type')}
                            className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition ${
                              basicSignatureMode === 'type' 
                                ? 'bg-white text-amber-800 shadow-sm' 
                                : 'text-amber-600 hover:text-amber-800'
                            }`}
                          >
                            <Type className="w-4 h-4" />
                            Type
                          </button>
                          <button
                            type="button"
                            onClick={() => setBasicSignatureMode('draw')}
                            className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition ${
                              basicSignatureMode === 'draw' 
                                ? 'bg-white text-amber-800 shadow-sm' 
                                : 'text-amber-600 hover:text-amber-800'
                            }`}
                          >
                            <Pen className="w-4 h-4" />
                            Draw
                          </button>
                        </div>
                        
                        {/* Type Signature */}
                        {basicSignatureMode === 'type' && (
                          <div className="relative">
                            <input
                              type="text"
                              value={basicTypedSignature}
                              onChange={(e) => {
                                setBasicTypedSignature(e.target.value);
                                setJobForm({
                                  ...jobForm,
                                  task_verification: {
                                    ...jobForm.task_verification,
                                    manager_signature: e.target.value,
                                    requirement_accepted: null
                                  }
                                });
                              }}
                              className="w-full px-4 py-4 border-2 border-amber-300 rounded-lg focus:ring-2 focus:ring-amber-500 bg-white text-2xl text-blue-800"
                              placeholder="Type your signature here"
                              style={{ fontFamily: "'Brush Script MT', 'Segoe Script', cursive", color: '#1e40af' }}
                              data-testid="job-signature-type-input"
                            />
                            {basicTypedSignature && (
                              <button
                                type="button"
                                onClick={() => {
                                  setBasicTypedSignature('');
                                  setJobForm({
                                    ...jobForm,
                                    task_verification: {
                                      ...jobForm.task_verification,
                                      manager_signature: '',
                                      requirement_accepted: null
                                    }
                                  });
                                }}
                                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600"
                              >
                                <X className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        )}
                        
                        {/* Draw Signature */}
                        {basicSignatureMode === 'draw' && (
                          <div className="space-y-2">
                            <div className="relative border-2 border-amber-300 rounded-lg bg-white overflow-hidden" style={{ maxWidth: '100%' }}>
                              <canvas
                                ref={basicCanvasRef}
                                width={350}
                                height={80}
                                onMouseDown={startBasicDrawing}
                                onMouseMove={drawBasic}
                                onMouseUp={stopBasicDrawing}
                                onMouseLeave={stopBasicDrawing}
                                onTouchStart={startBasicDrawing}
                                onTouchMove={drawBasic}
                                onTouchEnd={stopBasicDrawing}
                                className="w-full cursor-crosshair touch-none"
                                style={{ touchAction: 'none', maxHeight: '80px' }}
                              />
                              <div className="absolute bottom-1 left-3 text-xs text-gray-400 pointer-events-none">
                                Sign here
                              </div>
                              <div className="absolute bottom-0 left-0 right-0 h-px bg-gray-300 mx-4 mb-4"></div>
                            </div>
                            <button
                              type="button"
                              onClick={clearBasicCanvas}
                              className="text-sm text-amber-600 hover:text-amber-800 flex items-center gap-1"
                            >
                              <RefreshCw className="w-3 h-3" />
                              Clear signature
                            </button>
                          </div>
                        )}
                        
                        {/* Signature Preview */}
                        {jobForm.task_verification?.manager_signature && basicSignatureMode === 'type' && (
                          <div className="mt-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
                            <p className="text-xs text-gray-500 mb-1">Signature Preview:</p>
                            <p className="text-xl text-blue-800" style={{ fontFamily: "'Brush Script MT', 'Segoe Script', cursive" }}>
                              {jobForm.task_verification.manager_signature}
                            </p>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
                
                {/* Accept / Deny Buttons */}
                {jobForm.task_verification?.requirement_accepted === null && (
                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={() => {
                        let signature = jobForm.task_verification?.manager_signature || '';
                        if (basicSignatureMode === 'draw' && basicCanvasRef.current) {
                          signature = basicCanvasRef.current.toDataURL();
                        }
                        
                        if (!jobForm.task_verification?.manager_name) {
                          toast.error('Please enter your name first');
                          return;
                        }
                        if (!signature || (basicSignatureMode === 'draw' && signature === 'data:,')) {
                          toast.error('Please provide your signature first');
                          return;
                        }
                        
                        setJobForm({
                          ...jobForm,
                          task_verification: {
                            ...jobForm.task_verification,
                            manager_signature: signature,
                            requirement_accepted: true,
                            verified_at: new Date().toISOString()
                          }
                        });
                        toast.success('Task verification requirement accepted');
                      }}
                      className="flex-1 px-4 py-2.5 rounded-lg font-medium transition flex items-center justify-center gap-2 bg-green-500 text-white hover:bg-green-600"
                      data-testid="job-accept-verification-btn"
                    >
                      <CheckCircle className="w-4 h-4" />
                      Accept Requirement
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        let signature = jobForm.task_verification?.manager_signature || '';
                        if (basicSignatureMode === 'draw' && basicCanvasRef.current) {
                          signature = basicCanvasRef.current.toDataURL();
                        }
                        
                        if (!jobForm.task_verification?.manager_name) {
                          toast.error('Please enter your name first');
                          return;
                        }
                        if (!signature || (basicSignatureMode === 'draw' && signature === 'data:,')) {
                          toast.error('Please provide your signature first');
                          return;
                        }
                        
                        setJobForm({
                          ...jobForm,
                          task_verification: {
                            ...jobForm.task_verification,
                            manager_signature: signature,
                            requirement_accepted: false,
                            verified_at: new Date().toISOString()
                          }
                        });
                        toast.info('Task verification requirement denied');
                      }}
                      className="flex-1 px-4 py-2.5 rounded-lg font-medium transition flex items-center justify-center gap-2 bg-red-500 text-white hover:bg-red-600"
                      data-testid="job-deny-verification-btn"
                    >
                      <X className="w-4 h-4" />
                      Deny Requirement
                    </button>
                  </div>
                )}
                
                <p className="text-xs text-amber-600 mt-3">
                  * By signing, you acknowledge understanding of the task verification policy for this shift.
                </p>
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">Required Skills <span className="text-slate-400 font-normal">(comma-separated)</span></label>
              <input
                type="text"
                value={jobForm.required_skills}
                onChange={(e) => setJobForm({ ...jobForm, required_skills: e.target.value })}
                className="w-full h-12 px-4 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 focus:outline-none focus:border-blue-500 focus:bg-white transition-all"
                placeholder="RN License, CPR Certified, BLS"
                data-testid="job-skills-input"
              />
            </div>

            <div className="flex gap-3 pt-4 border-t border-slate-200">
              <button
                type="button"
                onClick={() => setShowJobForm(false)}
                className="flex-1 px-4 py-3 border border-slate-200 rounded-xl font-semibold text-slate-700 hover:bg-slate-50 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="flex-1 px-4 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl font-semibold hover:shadow-lg transition-all flex items-center justify-center gap-2"
                data-testid="job-submit-button"
              >
                <Plus className="w-5 h-5" />
                Post Job
              </button>
            </div>
          </form>
        </div>
      </div>
    )}
    </>
  );
}
